import React, { useEffect, useRef, useState } from "react";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { DataGrid } from "@mui/x-data-grid";
import { Typography, Tooltip, IconButton } from "@mui/material";
import Info from "../../assets/images/icons/Info.svg";
import "../Dynamic/UserList.scss";
import { CommonService } from "../../Service/CommonService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory, useLocation } from "react-router";
import EditIcon from "../../assets/images/icons/EditIcon.svg";
import EditIconWhite from "../../assets/images/icons/EditIconWhite.svg";
import Spinner from "../Common/Spinner";
import { Dropdown, ListGroup } from "react-bootstrap";
import Select from "react-select";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DataGridLoader from "../Common/DataGridLoader";

// Define the response type (optional but good practice)
interface QuarterPrices {
  [key: string]: number;
}

interface PriceByAgeRange {
  label: string;
  quarterPrices: QuarterPrices;
}

interface BarrelInsurancePricing {
  year: number;
  pricesByAgeRange: PriceByAgeRange[];
}

const InsurancePricingPanel: React.FC = () => {
  const history = useHistory();
  const [isLoading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [barrelInsurancePricing, setBarrelInsurancePricing] = useState<BarrelInsurancePricing | null>(null);
  const isEditButtonClicked = useRef(false);
  const [editedData, setEditedData] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const location = useLocation();
  const isEditMode = location.pathname.endsWith("/edit");

  useEffect(() => {
    getBarrelInsurancePricing(selectedYear);
  }, [location]);

  const handleEditClick = () => {
    const basePath = location.pathname.replace(/\/edit$/, ""); // remove trailing "/edit" if present

    const today = new Date();

    // Calculate the current quarter's start and end dates
    const currentQuarter = Math.floor((today.getMonth()) / 3) + 1;
    const quarterEndMonth = currentQuarter * 3; // Month is 1-based here (1-12)

    // JS Date month index is 0-based, so subtract 1
    const quarterEndDate = new Date(today.getFullYear(), quarterEndMonth, 0); // 0 means last day of the previous month
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Calculate the date 15 days before the end of the current quarter
    const last15DaysStart = new Date(quarterEndDate);
    last15DaysStart.setDate(quarterEndDate.getDate() - 14);

    const isInLast15Days = todayDateOnly >= last15DaysStart && todayDateOnly <= quarterEndDate;

    if (!isInLast15Days) {
      CommonService.Toast.fire({
          title:"The market value price can only be updated within the last 15 days of the final month of each quarter.",
          icon: "error"
      });
      return;
    }

    if (isEditMode) {
      history.push(basePath); // go back to view mode
      isEditButtonClicked.current = false;
    } else {
      history.push(`${basePath}/edit`); // switch to edit mode
      isEditButtonClicked.current = true;
    }
  };

  const handleExportClick = async () => {
    try {
      const response = await InventoryApiService.downloadMarketValueReport(CommonService.isUser() ? CommonService.getCompanyId() : null, selectedYear);
      CommonService.Toast.fire({
        title: "Your export is in progress. We'll notify you when it's ready!",
        icon: "success"
      });
      
    } catch (error) {
      console.log("Error occurred while exporting file")
    }
  }

  const currentYear = new Date().getFullYear();

  const yearOptions = Array.from(
    { length: currentYear + 4 - 2025 },
    (_, index) => {
      const year = 2025 + index;
      return { value: year, label: year };
    }
  );

  // Handle cancel edit
  const handleCancel = () => {
    // Reset to original data
    if (barrelInsurancePricing?.pricesByAgeRange) {
      const initialEditData = {};
      barrelInsurancePricing.pricesByAgeRange.forEach((row) => {
        initialEditData[row.label] = {
          q1: row.quarterPrices?.["1"] || "",
          q2: row.quarterPrices?.["2"] || "",
          q3: row.quarterPrices?.["3"] || "",
          q4: row.quarterPrices?.["4"] || "",
        };
      });
      setEditedData(initialEditData);
    }

    // Exit edit mode
    handleEditClick();
    // Force re-render
    setEditedData({ ...editedData });
  };

  const handleCellChange = (ageRange, quarter, value) => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-based (Jan=0)
    const currentQuarter = Math.floor(currentMonth / 3) + 1; // 1-based (Q1-Q4)

    const quarterNumber = parseInt(quarter.replace("q", ""), 10);

    // Disallow editing for current or past quarters
    if (quarterNumber <= currentQuarter) return;

    const numericValue = value === "" ? "" : Number(value);

    setEditedData((prev) => ({
      ...prev,
      [ageRange]: {
        ...prev[ageRange],
        [quarter]: numericValue,
      },
    }));
  };

  const handleBack = () => {
    history.push("/master/dashboard");
  };

  const getBarrelInsurancePricing = async (year: number) => {
    try {
      setLoading(true);
      const response = await InventoryApiService.getBarrelInsurancePricing(
        year
      );
      setBarrelInsurancePricing(response.data); // Save the response to state

      // Initialize editedData with current data
      if (response.data?.pricesByAgeRange) {
        const initialEditData = {};
        response.data.pricesByAgeRange.forEach((row) => {
          initialEditData[row.label] = {
            q1: row.quarterPrices?.["1"] || "",
            q2: row.quarterPrices?.["2"] || "",
            q3: row.quarterPrices?.["3"] || "",
            q4: row.quarterPrices?.["4"] || "",
          };
        });
        setEditedData(initialEditData);
      }
    } catch (error) {
      console.error("Failed to fetch container insurance pricing", error);
    } finally {
      setLoading(false);
    }
  };

  const quarterLabels = {
    "1": "Q1 (Jan-Mar)",
    "2": "Q2 (Apr-Jun)",
    "3": "Q3 (Jul-Sep)",
    "4": "Q4 (Oct-Dec)",
  };

  // Create columns based on edit mode
  const columns = [
    {
      field: "label",
      headerName: "Container Age Group (In Years)",
      sortable: false, // Disable sorting
      flex: 1,
      minWidth: 180,
      headerClassName: "table-header py-2 h-100 market-value-cell",
      editable: false,
    },
    ...["1", "2", "3", "4"].map((quarter) => ({
      field: `q${quarter}`, // use backticks
      headerName: quarterLabels[quarter],
      flex: 1,
      minWidth: 120,
      sortable: false, // Disable sorting
      headerClassName: "table-header py-2 h-100 market-value-cell",
      renderCell: (params) => {
        const fieldKey = `q${quarter}`;
      
        const today = new Date();
        const currentMonth = today.getMonth(); // 0 = Jan
        const currentDate = today.getDate();


        const inputQuarterNumber = parseInt(quarter, 10);
        const previousQuarterNumber = inputQuarterNumber - 1;
      
        // Edge case: Q1 follows Q4 (Dec)
        const prevQuarterEndMonth =
          previousQuarterNumber === 0 ? 11 : (previousQuarterNumber - 1) * 3 + 2;
      
        const isEditableDate =
          currentMonth === prevQuarterEndMonth && currentDate >= 16;
      
        if (isEditMode) {
          return (
            <div className="d-flex position-relative align-items-center h-100">
              <span
                className="position-absolute text-muted"
                style={{
                  left: "8px",
                  top: "calc(50% - 1px)",
                  fontSize: "1rem",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  zIndex: 1,
                  color: "#212529",
                }}
              >
                $
              </span>
              <input
                type="number"
                className="form-control"
                value={editedData?.[params.row.label]?.[fieldKey] ?? ""}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                  // Allow only numbers with up to 2 decimal places
                  if (/^\d*\.?\d{0,2}$/.test(rawValue)) {
                    handleCellChange(params.row.label, fieldKey, rawValue);
                  }
                }}
                disabled={!isEditableDate}
                style={{
                  minWidth: "70px",
                  maxWidth: "100px",
                  borderBottom: "1px solid #D1D1D1",
                  paddingLeft: "18px",
                  paddingBlock: "5px",
                }}
              />
            </div>
          );
        } else {
          return (
            <div>
              {params.value !== "-" && params.value !== null ? `$${Number(params.value).toFixed(2)}` : "-"}
            </div>
          );
        }
      }   
      // renderCell: (params) => {
      //   const fieldKey = `q${quarter}`;
      //   const today = new Date();
      //   const currentMonth = today.getMonth(); // 0 = Jan

      //   // Determine current quarter (Q1 = Jan-Mar, Q2 = Apr-Jun, etc.)
      //   const currentQuarter = Math.floor(currentMonth / 3) + 1;
      //   const inputQuarterNumber = parseInt(quarter, 10);

      //   // Allow input only for future quarters
      //   const isEditableQuarter = inputQuarterNumber > currentQuarter;

      //   if (isEditMode) {
      //     return (
      //       <div className="d-flex position-relative align-items-center h-100">
      //         <span
      //           className="position-absolute text-muted"
      //           style={{
      //             left: "8px",
      //             top: "calc(50% - 1px)",
      //             fontSize: "1rem",
      //             transform: "translateY(-50%)",
      //             pointerEvents: "none",
      //             zIndex: 1,
      //             color: "#212529",
      //           }}
      //         >
      //           $
      //         </span>
      //         <input
      //           type="number"
      //           className="form-control"
      //           value={editedData?.[params.row.label]?.[fieldKey] ?? ""}
      //           onChange={(e) => {
      //             const rawValue = e.target.value.replace(/[^0-9.]/g, "");
      //             if (/^\d*\.?\d{0,2}$/.test(rawValue)) {
      //               handleCellChange(params.row.label, fieldKey, rawValue);
      //             }
      //           }}
      //           // disabled={!isEditableQuarter}
      //           style={{
      //             minWidth: "70px",
      //             maxWidth: "100px",
      //             borderBottom: "1px solid #D1D1D1",
      //             paddingLeft: "18px",
      //             paddingBlock: "5px",
      //           }}
      //         />
      //       </div>
      //     );
      //   } else {
      //     return (
      //       <div>
      //         {params.value !== "-" && params.value !== null
      //           ? `$${Number(params.value).toFixed(2)}`
      //           : "-"}
      //       </div>
      //     );
      //   }
      // }
     
    })),
  ];

  // Define a clearer transformation function
  const transformedRows = (barrelInsurancePricing?.pricesByAgeRange || []).map(
    (row) => {
      // Get the quarterly prices (or empty object if missing)
      const quarterPrices = row.quarterPrices || {};

      // Create the transformed row with explicit handling for each field
      return {
        label: row.label,
        q1: quarterPrices["1"] !== undefined ? quarterPrices["1"] : "-",
        q2: quarterPrices["2"] !== undefined ? quarterPrices["2"] : "-",
        q3: quarterPrices["3"] !== undefined ? quarterPrices["3"] : "-",
        q4: quarterPrices["4"] !== undefined ? quarterPrices["4"] : "-",
      };
    }
  );

  // Handle saving edited data
  const handleSave = async () => {

    // Get current quarter (1–4)
    const currentMonth = new Date().getMonth() + 1;
    const currentQuarter = Math.floor((currentMonth - 1) / 3) + 1;

    // Calculate next quarter (1 → 2, 4 → 1)
    const nextQuarter = currentQuarter === 4 ? 1 : currentQuarter + 1;

    // Check if next quarter value is filled for all rows
    const isNextQuarterMissing = barrelInsurancePricing.pricesByAgeRange.some((row) => {
      const data = editedData[row.label] || {};
      const nextQuarterValue = data[`q${nextQuarter}`];
      return !nextQuarterValue;
    });

    if (isNextQuarterMissing) {
      CommonService.Toast.fire({
        title: `Please provide values for Q${nextQuarter} in every age range.`,
        icon: "error",
      });
      return;
    }

    // Transform the edited data back to the format expected by the API
    const updatedPricing = {
      year: barrelInsurancePricing.year,
      pricesByAgeRange: barrelInsurancePricing.pricesByAgeRange.map((row) => {
        return {
          label: row.label,
          quarterPrices: {
            "1": editedData[row.label]?.q1 || "",
            "2": editedData[row.label]?.q2 || "",
            "3": editedData[row.label]?.q3 || "",
            "4": editedData[row.label]?.q4 || "",
          },
        };
      }),
    };

    try {
      setIsUpdating(true);
      // Call API to save data
      await InventoryApiService.saveInsurancePrice(updatedPricing);

      // Exit edit mode
      handleEditClick();
      setEditedData({ ...editedData });

      //show the success message
      CommonService.Toast.fire({
        title: "Your container market values has been updated successfully",
        icon: "success",
      });
    } catch (error) {
      console.error("Failed to save container insurance pricing", error);
      const errorMessage = error?.response?.data?.errorMessage ?? "";
      CommonService.Toast.fire({
        title: `${errorMessage}`,
        icon: "error",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="usersList pb-5">
      <div className="usersList__header--wrapper d-flex flex-wrap align-items-center gap-2">
        <div className="d-flex align-items-center gap-2">
          <IconButton
            onClick={handleBack}
            aria-label="ArrowBackIcon"
            className="mb-0"
            sx={{
              backgroundColor: "#FFF",
              mb: 1,
              "&:hover": { backgroundColor: "#FFF" },
            }}
          >
            <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
          </IconButton>
          <div className="header--title d-flex align-items-center">
            <h1 className="title mb-0 d-inline-block">
              Container Market Value Management
            </h1>
            <div className="company-dashboard__user--companyInfo  px-2 d-inline-block">
              <Dropdown>
                <Dropdown.Toggle variant="info">
                  <img src={Info} alt="Info" />
                </Dropdown.Toggle>

                <Dropdown.Menu className="barrel-market-tooltip">
                  <ListGroup>
                    <ListGroup.Item>
                      <div className="company-details">
                        <p className="mb-2">
                          You can set the per-container pricing based on market
                          value. This value will be used as the recommended
                          amount for customers.
                        </p>
                        <p className="mb-0">
                          If a customer does not provide a custom value, the
                          insurance cost will be calculated based on this
                          recommended amount.
                        </p>
                      </div>
                    </ListGroup.Item>
                  </ListGroup>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <div className="divider mx-2"></div>
        </div>

        <div className="header--menu">
          <div className="header--menu-items d-flex align-items-center flex-wrap gap-2">
            <button title="Export to Excel"
              className="primary-btn d-inline-flex align-items-center"
              style={{ paddingTop:"4px",paddingLeft:11}}
              onClick={handleExportClick}
            >
            <FileDownloadOutlinedIcon sx={{marginTop:"5.5px"}}/>
            Export
          </button>
            {/* <button
              className={`white-outline-btn d-inline-flex justify-content-between align-items-center gap-1 ${
                isEditButtonClicked.current ? "bg-white" : ""
              }`}
              onClick={handleEditClick}
            >
              Edit
              <img
                className="cursor-pointer"
                src={isEditButtonClicked ? EditIcon : EditIconWhite}
                alt="EditIconWhite"
                height={16}
              />
            </button> */}
            <IconButton
              aria-label="message"
              size="small"
              onClick={handleEditClick}
              sx={{mx:0,
                borderRadius: "50%", backgroundColor: isEditButtonClicked
                  ? "#97AEB8" // When only `isOpenEditSection` is true
                  : "#fff", color: "#1C1B1F", marginInline: "8px", padding: "8px"
              }}

            >
              {isEditButtonClicked
                ? <img className="cursor-pointer"
                  src={EditIconWhite}
                  alt="EditIconWhite"
                /> : <img className="cursor-pointer"
                  src={EditIcon}
                  alt="EditIcon"
                />
              }
            </IconButton>
            <div>
              <select
                id="year"
                className="form-select"
                value={selectedYear}
                style={{ width: 'auto' }}
                onChange={(e) => {
                  const selectedYear = Number(e.target.value);
                  setSelectedYear(selectedYear);
                  getBarrelInsurancePricing(selectedYear);
                }}
              >
                {yearOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="user-table-container market-value-table-wrapper">
        <div className="userTable">
          <DataGrid
            rows={transformedRows}
            className="data-grid support-req--listing market-value-datatable"
            columns={columns}
            getRowId={(row) => row.label}
            disableColumnMenu
            hideFooter={transformedRows.length <= 10}
            disableColumnResize
            loading={isLoading}
            slots={{
              loadingOverlay: DataGridLoader,
            }}
            disableRowSelectionOnClick
            showColumnVerticalBorder
            showCellVerticalBorder
          />
          {isEditMode && (
            <div className="btn-group d-flex justify-content-center w-100 gap-3">
              <button className="primary-btn" onClick={handleSave}>
                {isUpdating && <Spinner />} Update
              </button>
              <button className="secondary-btn" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsurancePricingPanel;
